// SearchInput.jsx
import React, { useRef, useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color, textColor } from '../style/variables.js';

import focusAndOpenKeyboard from '../resource/focusAndOpenKeyboard.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';

import SearchIconSource from '../../img/ic_search_white.svg';
import CloseIconSource from '../../img/ic_search_close.svg';
import { ButtonId } from '../resource/trackingConstants.js';
import media from '../style/media.js';

const defaultFunction = () => null;

// Rotate the VIP recommend placeholder every 3s when more than one is set.
const RECOMMEND_PLACEHOLDER_INTERVAL_MS = 3000;

export const SearchInput = ({
  onChange = defaultFunction,
  onFocus = defaultFunction,
  onBlur = defaultFunction,
  onSearchClear = defaultFunction,
  value = '',
  placeholderI18nKey = 'tabbar_search',
  searchColor = textColor.white,
  isFocus = false,
  shouldShowSearchModal = false,
  shouldAutoFocus = false,
  recommendUsers = EMPTY_ARRAY,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  useEffect(() => {
    if (shouldAutoFocus && inputRef.current) {
      inputRef.current.focus();
      focusAndOpenKeyboard({
        element: inputRef.current,
        delayedMilliseconds: 300,
      });
    }
  }, [shouldAutoFocus]);

  const hasValue = value != null && value !== '';
  const shouldDisplayClearButton = shouldShowSearchModal
    ? isFocus && hasValue
    : hasValue;
  const shouldDisplaySearchIcon =
    shouldShowSearchModal || (!isFocus && !hasValue);
  // Desktop active style
  const shouldDisplayActiveStyle =
    !shouldShowSearchModal && (isFocus || hasValue);

  // VIP recommend placeholder shows "displayName @username", or just
  // "@username" when the user has no displayName. recommendUsers come from
  // getVipRecommendUsers, which already excludes users without a username.
  const recommendLabels = useMemo(
    () =>
      recommendUsers.map(user =>
        user.displayName
          ? `${user.displayName} @${user.username}`
          : `@${user.username}`
      ),
    [recommendUsers]
  );

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const shouldRotatePlaceholder = recommendLabels.length >= 2 && !hasValue;
  const shouldShowRecommendPlaceholder =
    recommendLabels.length > 0 && !hasValue;
  const recommendPlaceholder =
    recommendLabels[placeholderIndex % recommendLabels.length] || '';

  useEffect(() => {
    if (!shouldRotatePlaceholder) return;
    const timer = setInterval(() => {
      setPlaceholderIndex(
        previousIndex => (previousIndex + 1) % recommendLabels.length
      );
    }, RECOMMEND_PLACEHOLDER_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [shouldRotatePlaceholder, recommendLabels.length]);

  // When the recommend placeholder overlay is shown, keep the native
  // placeholder empty so only the animated overlay is visible.
  const nativePlaceholder = useMemo(() => {
    if (recommendLabels.length > 0) {
      return '';
    }
    return t(placeholderI18nKey, {
      ns: TranslationNamespace.GENERAL,
    });
  }, [recommendLabels.length, placeholderI18nKey, t]);

  return (
    <StyledSearchInput
      searchColor={searchColor}
      shouldActive={shouldDisplayActiveStyle}
    >
      {shouldDisplaySearchIcon && (
        <IconWrapper>
          <img src={SearchIconSource} alt="search" width="20" height="20" />
        </IconWrapper>
      )}
      <InputWrapper>
        <input
          ref={inputRef}
          value={value}
          type="text"
          enterKeyHint="search"
          placeholder={nativePlaceholder}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {shouldShowRecommendPlaceholder && (
          <RecommendPlaceholder $isFocus={isFocus}>
            <RecommendPlaceholderText key={recommendPlaceholder}>
              {recommendPlaceholder}
            </RecommendPlaceholderText>
          </RecommendPlaceholder>
        )}
      </InputWrapper>
      {shouldDisplayClearButton && (
        <ClearInputButton
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={onSearchClear}
          data-element_id={ButtonId.Search.ButtonSearchCancel}
          aria-label="clear input"
        >
          <img src={CloseIconSource} width={20} height={20} alt="" />
        </ClearInputButton>
      )}
    </StyledSearchInput>
  );
};

SearchInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchColor: PropTypes.string,
  placeholderI18nKey: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onSearchClear: PropTypes.func,
  isFocus: PropTypes.bool,
  shouldShowSearchModal: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  recommendUsers: PropTypes.array,
};

export const IconWrapper = styled.div`
  flex: none;
  height: 20px;

  & > img {
    height: 100%;
    vertical-align: top;
  }
`;

export const StyledSearchInput = styled.label`
  position: relative;
  width: 100%;

  height: 40px;
  border-radius: 20px;
  ${media.mobile`
    height: 30px;
    border-radius: 15px;
  `}
  margin-bottom: 0;
  padding: 4px 12px 4px 16px;
  background-color: ${({ shouldActive }) =>
    shouldActive ? color.black : color.neutral[20]};
  border: solid 1px ${color.neutral[20]};

  display: flex;
  align-items: center;

  & input[type='text'] {
    flex: auto;
    outline: none;
    border: none;
    padding: 0px 8px 0px 4px;
    height: 100%;
    width: 100%;
    background: none;
    font-size: 16px;
    font-weight: 400;
    color: ${({ searchColor }) => searchColor};
    line-height: 16px;
    ::placeholder {
      color: ${textColor.white};
    }
    :focus::placeholder {
      color: ${textColor.black};
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  flex: auto;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
`;

const slideUpKeyframes = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const RecommendPlaceholder = styled.div`
  position: absolute;
  left: 4px;
  right: 8px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  pointer-events: none;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ $isFocus }) => ($isFocus ? textColor.black : textColor.white)};
`;

const RecommendPlaceholderText = styled.span`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.5;
  animation: ${slideUpKeyframes} 400ms ease;
`;

const ClearInputButton = styled.button`
  ${ResetButtonStyle}
  border-radius: 50%;
  width: 20px;
  height: 20px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral['20_alpha_80']};
`;

export default memo(SearchInput);
